import { useState, useCallback } from "react";
import { toast } from "sonner";
import { useGlobalContext } from "../../GlobalContext";

export default function useDriverCharges() {
  const { api } = useGlobalContext();

  const [chargeRows, setChargeRows] = useState([]);
  const [chargesLoading, setChargesLoading] = useState(false);

  const shouldShowLocalError = (data) => {
    return (
      data?.responseCode !== "00" &&
      data?.responseCode !== "02" &&
      data?.responseCode !== "03" &&
      data?.responseCode !== "98" &&
      data?.responseCode !== "401"
    );
  };

  /* ================= GET CHARGES FOR SINGLE DRIVER ================= */

  const getDriverChargesApi = useCallback(
    async (driverCode = "") => {
      try {
        const res = await api.post("/api/Transactions/v1.0/getDriverCharges", {
          driverCode,
        });

        if (res.data?.responseCode === "00") {
          return res.data?.data || [];
        }

        if (shouldShowLocalError(res.data)) {
          toast.error(res.data?.responseMessage || "Failed to fetch charges");
        }

        return [];
      } catch (error) {
        console.error("getDriverChargesApi error:", error);
        toast.error("Error fetching driver charges");
        return [];
      }
    },
    [api]
  );

  const fetchDriverCharges = useCallback(async () => {
    setChargesLoading(true);

    try {
      const data = await getDriverChargesApi("");

      const normalized = (data || []).map((group) => {
        const charge = group?.charges?.[0] || {};

        const perDayAmount = Number(charge?.perDayAmount || 0);
        const perOrderAmount = Number(charge?.perOrderAmount || 0);

        return {
          driverCode: group?.driverCode || "",
          name: group?.name || "",
          teamName: group?.teamName || "",
          chargeId: charge?.id || null,
          perDayAmount,
          perOrderAmount,
          hasCharges: perDayAmount > 0 && perOrderAmount > 0,
        };
      });

      setChargeRows(normalized);
      return normalized;
    } catch (error) {
      console.error("fetchDriverCharges error:", error);
      setChargeRows([]);
      return [];
    } finally {
      setChargesLoading(false);
    }
  }, [getDriverChargesApi]);

  /* ================= ADD / UPDATE DRIVER CHARGES ================= */

  const saveDriverChargeApi = async ({
    adminId,
    driverCode,
    perDayAmount,
    perOrderAmount,
  }) => {
    if (!adminId || !driverCode) {
      toast.error("Missing required fields");
      return null;
    }

    const payload = {
      adminId: String(adminId),
      driverCode: String(driverCode),
      type1: "per_day",
      type1Amount: String(perDayAmount ?? 0),
      type2: "per_order",
      type2Amount: String(perOrderAmount ?? 0),
    };

    try {
      const res = await api.post(
        "/api/Transactions/v1.0/addDriverCharges",
        payload
      );

      if (res.data?.responseCode === "00") {
        toast.success(res.data?.responseMessage || "Driver charges saved");
        return res.data;
      }

      if (shouldShowLocalError(res.data)) {
        toast.error(res.data?.responseMessage || "Failed to save charges");
      }

      return null;
    } catch (error) {
      console.error("saveDriverChargeApi error:", error);
      toast.error("Error saving driver charges");
      return null;
    }
  };

  return {
    chargeRows,
    chargesLoading,
    fetchDriverCharges,
    getDriverChargesApi,
    saveDriverChargeApi,
  };
}